import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable, ServiceLevel } from '@rygen/rygen-ui/model';

import api from '@/api';

import { useCurrentUserStore } from './CurrentUser';

export interface PerformLoadOptions {
  bypassCache: boolean;
}

export interface ServiceLevelStore {
  loadServiceLevels: (opts?: PerformLoadOptions) => Promise<void>;
  serviceLevels: Ref<Loadable<ServiceLevel[]>>;
}

const PERFORM_LOAD_DEFAULT_OPTIONS: PerformLoadOptions = {
  bypassCache: false,
};

const _useServiceLevelStore = defineStore(
  'serviceLevel',
  (): ServiceLevelStore => {
    const { selectedDomainId } = useCurrentUserStore();
    const { t } = useI18n();
    const toast = useToast();

    const serviceLevels: Ref<Loadable<ServiceLevel[]>> = ref({
      loading: false,
    });

    const loadServiceLevels = async (
      opts: PerformLoadOptions = PERFORM_LOAD_DEFAULT_OPTIONS,
    ) => {
      const options = { ...PERFORM_LOAD_DEFAULT_OPTIONS, ...opts };

      const numServiceLevels = serviceLevels.value.data?.length ?? 0;
      const isLoading = serviceLevels.value.loading;
      const requiresLoad = numServiceLevels === 0 && !isLoading;

      if (options.bypassCache || requiresLoad) {
        return performLoad();
      }
    };

    const performLoad = async () => {
      try {
        serviceLevels.value.loading = true;
        serviceLevels.value.data = await api.serviceLevel.findAll();
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        serviceLevels.value.error = e;
        console.error('Error loading service levels', e);
        toast.addError({
          detail: t('errors.loadingParam', {
            param: t('carriers.serviceLevels', 2),
          }),
          error: e,
        });
      } finally {
        serviceLevels.value.loading = false;
      }
    };

    watch(selectedDomainId, () => {
      serviceLevels.value.data = [];
    });

    return {
      loadServiceLevels,
      serviceLevels,
    };
  },
);

export const useServiceLevelStore = (): ServiceLevelStore => {
  const store = _useServiceLevelStore();
  const { serviceLevels } = storeToRefs(store);
  const { loadServiceLevels } = store;

  return {
    loadServiceLevels,
    serviceLevels,
  };
};
